import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CheckpointCard from '../components/CheckpointCard';
import { TypewriterText } from '../hooks/useTypewriter';

const API = '/api';

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const TABS = [
  { key: 'checkpoints', label: 'models' },
  { key: 'creators', label: 'creators' },
];

export default function Checkpoints() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'creators' ? 'creators' : 'checkpoints';
  const [tab, setTab] = useState(initialTab);

  const switchTab = (t) => {
    setTab(t);
    const next = new URLSearchParams(searchParams);
    if (t === 'creators') next.set('tab', 'creators'); else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-black py-12 px-6 lg:px-8 page-wrapper" data-testid="search-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase mb-8 animate-slide-up">
          <TypewriterText text="search" speed={70} startDelay={150} />
        </h1>

        {/* Tabs */}
        <div className="flex items-center gap-6 mb-8 border-b border-[#1e251f]">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`lowercase text-sm pb-3 -mb-px border-b transition-colors duration-200 ${
                tab === t.key
                  ? 'text-white border-white'
                  : 'text-[#555555] border-transparent hover:text-[#999999]'
              }`}
              data-testid={`search-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'checkpoints' ? <CheckpointsTab /> : <CreatorsTab />}
      </div>
    </div>
  );
}

// ====================== CHECKPOINTS TAB ======================

function CheckpointsTab() {
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sort, setSort] = useState('downloads');
  const [allTags, setAllTags] = useState([]);
  const [nBands, setNBands] = useState('');
  const [hiddenDim, setHiddenDim] = useState('');
  const [minBytes, setMinBytes] = useState('');

  const dSearch = useDebounce(search, 300);
  const dNBands = useDebounce(nBands, 300);
  const dHiddenDim = useDebounce(hiddenDim, 300);
  const dMinBytes = useDebounce(minBytes, 300);

  useEffect(() => {
    fetch(`${API}/tags`).then(r => r.ok ? r.json() : []).then(setAllTags).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dSearch) params.append('search', dSearch);
    if (selectedTags.length) params.append('tags', selectedTags.join(','));
    if (verifiedOnly) params.append('verified_only', 'true');
    if (sort) params.append('sort', sort);
    if (dNBands) params.append('n_bands', dNBands);
    if (dHiddenDim) params.append('hidden_dim', dHiddenDim);
    if (dMinBytes) params.append('min_bytes', dMinBytes);
    fetch(`${API}/checkpoints?${params.toString()}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('failed to fetch checkpoints')))
      .then(setCheckpoints)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [dSearch, selectedTags, verifiedOnly, sort, dNBands, dHiddenDim, dMinBytes]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  return (
    <>
      <div className="mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="search by name, tag, or creator"
          className="w-full bg-[#0e120f] border border-[#272e28] text-white px-6 py-4 focus:border-[#4a514a] transition-all duration-300 lowercase rounded-xl"
          data-testid="search-input"
        />
      </div>

      <div className="border-y border-[#1e251f] py-6 mb-6 space-y-6">
        {allTags.length > 0 && (
          <div>
            <label className="block font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-2">tags</label>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`tag-chip cursor-pointer ${selectedTags.includes(tag) ? 'border-white text-white bg-white/10' : ''}`}
                  data-testid={`tag-filter-${tag}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-2">width</label>
            <input type="number" value={hiddenDim} onChange={e => setHiddenDim(e.target.value)} placeholder="e.g. 8192"
              className="w-full bg-[#0e120f] border border-[#272e28] text-white px-4 py-3 focus:border-[#4a514a] transition-all duration-300 rounded-lg"
              data-testid="filter-hiddendim" />
          </div>
          <div>
            <label className="block font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-2">bands</label>
            <input type="number" value={nBands} onChange={e => setNBands(e.target.value)} placeholder="e.g. 46"
              className="w-full bg-[#0e120f] border border-[#272e28] text-white px-4 py-3 focus:border-[#4a514a] transition-all duration-300 rounded-lg"
              data-testid="filter-nbands" />
          </div>
          <div>
            <label className="block font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-2">at least this many bytes seen</label>
            <input type="number" value={minBytes} onChange={e => setMinBytes(e.target.value)} placeholder="e.g. 1000000"
              className="w-full bg-[#0e120f] border border-[#272e28] text-white px-4 py-3 focus:border-[#4a514a] transition-all duration-300 rounded-lg"
              data-testid="filter-min-bytes" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input type="checkbox" checked={verifiedOnly} onChange={e => setVerifiedOnly(e.target.checked)}
              className="w-5 h-5 bg-black border border-[#383f38] accent-white rounded" data-testid="verified-toggle" />
            <span className="text-[#888888] lowercase">verified only</span>
          </label>
          <div className="flex items-center space-x-3">
            <label className="text-[#888888] lowercase">sort:</label>
            <select value={sort} onChange={e => setSort(e.target.value)}
              className="bg-[#0e120f] border border-[#272e28] text-white px-4 py-2 focus:border-[#4a514a] transition-all duration-300 lowercase rounded-lg"
              data-testid="sort-select">
              <option value="downloads">most downloaded</option>
              <option value="stars">most starred</option>
              <option value="newest">newest</option>
              <option value="trained">most trained</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-24"><p className="text-[#888888] lowercase animate-pulse">loading models…</p></div>
      ) : error ? (
        <div className="text-center py-24"><p className="text-[#888888] lowercase">{error}</p></div>
      ) : checkpoints.length === 0 ? (
        <div className="text-center py-24" data-testid="empty-state">
          <p className="text-2xl text-white lowercase mb-4">nothing matches.</p>
          <Link to="/upload" className="inline-block text-[#888888] lowercase hover:text-white transition-all duration-300" data-testid="upload-cta">
            upload a model →
          </Link>
        </div>
      ) : (
        <div className="divide-y divide-[#1a201b]" data-testid="checkpoints-grid">
          {checkpoints.map(c => <CheckpointCard key={c.checkpoint_id} checkpoint={c} />)}
        </div>
      )}
    </>
  );
}

// ====================== CREATORS TAB ======================

const CREATOR_SORTS = [
  { key: 'stars', label: 'most starred' },
  { key: 'downloads', label: 'most downloaded' },
  { key: 'checkpoints', label: 'most uploaded' },
  { key: 'followers', label: 'most followed' },
  { key: 'newest', label: 'newest' },
];

function CreatorsTab() {
  const [creators, setCreators] = useState([]);
  const [sort, setSort] = useState('stars');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/creators?sort=${sort}&limit=200`)
      .then(r => r.ok ? r.json() : [])
      .then(setCreators)
      .finally(() => setLoading(false));
  }, [sort]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? creators.filter(c =>
        (c.username || '').toLowerCase().includes(q) ||
        (c.bio || '').toLowerCase().includes(q))
    : creators;

  return (
    <>
      <div className="mb-6">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="search creators by username or bio"
          className="w-full bg-[#0e120f] border border-[#272e28] text-white px-6 py-4 focus:border-[#4a514a] transition-all duration-300 lowercase rounded-xl"
          data-testid="creators-search"
        />
      </div>

      <div className="flex items-center gap-3 text-sm mb-8">
        <label className="text-[#555555] lowercase">sort:</label>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          className="bg-[#0e120f] border border-[#272e28] text-white px-3 py-1.5 rounded-lg lowercase focus:border-[#4a514a]"
          data-testid="creators-sort"
        >
          {CREATOR_SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      <div className="max-w-3xl">
        {loading ? (
          <p className="text-[#555555] lowercase text-sm animate-pulse py-8">loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-[#555555] lowercase py-12 text-center">no creators match.</p>
        ) : (
          <ol className="divide-y divide-[#141414]" data-testid="creators-list">
            {filtered.map((c, i) => (
              <li key={c.user_id} className="py-4 flex items-center gap-4" data-testid={`creator-row-${c.username}`}>
                <span className="text-[#333333] text-xs tabular-nums w-8 shrink-0 text-right">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <Link to={`/u/${c.username}`} className="text-white font-bold lowercase hover:underline underline-offset-4 decoration-[#444444]">
                      {c.username}
                    </Link>
                    {c.verified && <img src="/phi-logo-64.png" alt="verified" title="verified" className="verified-badge" />}
                  </div>
                  {c.bio && <p className="text-[#888888] lowercase text-xs truncate mt-0.5">{c.bio}</p>}
                </div>
                <div className="hidden sm:flex items-center gap-5 text-xs text-[#888888] shrink-0">
                  <Stat value={c.total_stars} label="stars" />
                  <Stat value={c.total_downloads} label="dls" />
                  <Stat value={c.checkpoint_count} label="ckpts" />
                  <Stat value={c.follower_count} label="followers" />
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </>
  );
}

function Stat({ value, label }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-white tabular-nums">{(value || 0).toLocaleString()}</span>
      <span className="text-[#555555] lowercase">{label}</span>
    </div>
  );
}
